import { create } from 'zustand';
import { api } from '@/api/client';

export type Appearance = { fontSize: number; lineHeight: number; gap: number };
export const DEFAULT_APPEARANCE: Appearance = { fontSize: 14, lineHeight: 21, gap: 10 };
export const APPEARANCE_PRESETS: { key: string; label: string; value: Appearance }[] = [
  { key: 'compact', label: '紧凑', value: { fontSize: 12, lineHeight: 18, gap: 6 } },
  { key: 'standard', label: '标准', value: DEFAULT_APPEARANCE },
  { key: 'relaxed', label: '宽松', value: { fontSize: 16, lineHeight: 26, gap: 14 } },
  { key: 'large', label: '大字', value: { fontSize: 18, lineHeight: 30, gap: 18 } },
];

const localKey = (userId: string) => `ims.v2.appearance.${userId}`;
let saveQueue: Promise<unknown> = Promise.resolve();
const isAppearance = (v: unknown): v is Appearance => {
  if (!v || typeof v !== 'object') return false;
  const a = v as Partial<Appearance>;
  return Number.isInteger(a.fontSize) && a.fontSize! >= 12 && a.fontSize! <= 18
    && Number.isInteger(a.lineHeight) && a.lineHeight! >= 18 && a.lineHeight! <= 30
    && Number.isInteger(a.gap) && a.gap! >= 6 && a.gap! <= 18;
};

type State = {
  owner: string | null;
  value: Appearance;
  loaded: boolean;
  setValue: (value: Appearance) => void;
  load: (userId: string) => Promise<void>;
  save: (userId: string, value: Appearance) => Promise<'synced' | 'local'>;
};

export const useAppearance = create<State>((set, get) => ({
  owner: null,
  value: DEFAULT_APPEARANCE,
  loaded: false,
  setValue(value) { if (isAppearance(value)) set({ value }); },
  async load(userId) {
    set({ owner: userId, loaded: false, value: DEFAULT_APPEARANCE });
    let local: Appearance | null = null;
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(localKey(userId)) || 'null');
      if (isAppearance(parsed)) local = parsed;
    } catch { /* storage unavailable */ }
    if (local) set({ value: local });
    if (import.meta.env.VITE_READONLY !== '0' && local) {
      set({ loaded: true });
      return;
    }
    try {
      const remote = await api.get<Appearance>('/api/v2/me/appearance');
      if (get().owner === userId) set({ value: isAppearance(remote) ? remote : DEFAULT_APPEARANCE, loaded: true });
    } catch {
      if (get().owner === userId) set({ loaded: true });
    }
  },
  async save(userId, value) {
    if (!isAppearance(value)) throw new Error('外观参数超出范围');
    if (get().owner === userId) set({ value });
    try { localStorage.setItem(localKey(userId), JSON.stringify(value)); } catch { /* continue with server */ }
    if (import.meta.env.VITE_READONLY !== '0') return 'local';
    try {
      const pending = saveQueue.catch(() => undefined).then(() => api.patch<Appearance>('/api/v2/me/appearance', value));
      saveQueue = pending;
      await pending;
      return 'synced';
    } catch { return 'local'; }
  },
}));
